import React from 'react';
import { ExtensionPointProvider } from 'view/common/forge-ui/extension-point-provider';
import { ExtensionProvider } from 'view/common/forge-ui/extension-provider';
import { IframeRenderer } from 'view/common/forge-ui/iframe-renderer';
import { Renderer } from 'view/common/forge-ui/renderer';
import type {
    Extension,
    ExtensionData,
    ForgePageName,
    ForgeRunnerProps,
    ForgeRunnerPropsWithExtension,
    RunnerExtensionData,
    WithBlockedAppsBannerProps,
} from 'view/common/forge-ui/types';
import { withExtensionList } from 'view/common/forge-ui/with-extension-list';
import { Headline } from 'view/forge-ui/request-details-main-section/styled';
import { FORGE_HELP_CENTER } from 'view/common/forge-ui/constants';
import { StyledForgeContainer } from 'view/forge-ui/forge-runner/styled';
import { getMetaFromHTML } from 'util/meta';
import { isAnonymousCustomUIEnabled } from 'feature-flags';

import { isCustomUIExtension } from 'util/forge-ui';
import { useShowBanner } from 'util/blocked-apps-banner';

export const ForgeRunnerReactFragment: React.FC<ForgeRunnerPropsWithExtension> = ({
    extension,
    type,
    page,
    portalId,
    requestKey,
    requestTypeId,
}) => {
    const pages = extension.properties.pages || [];

    const extensionData: ExtensionData = React.useMemo(() => {
        const accountType = getMetaFromHTML('ajs-account-type') || 'anonymous';

        const computedExtensionData: RunnerExtensionData = {
            page,
            type,
            ...(isAnonymousCustomUIEnabled() && { accountType }),
        };

        if (!!portalId) {
            computedExtensionData.portal = { id: portalId };
        }

        if (requestKey || requestTypeId) {
            computedExtensionData.request = {
                ...(requestKey && { key: requestKey }),
                ...(requestTypeId && { typeId: requestTypeId }),
            };
        }

        return computedExtensionData;
    }, [page, portalId, requestKey, requestTypeId, type]);

    return pages.includes(page) || pages.length === 0 ? (
        <React.Fragment key={extension.id}>
            <Headline>{extension.properties.title}</Headline>
            <ExtensionProvider extension={extension}>
                {isCustomUIExtension(extension) ? (
                    <IframeRenderer extension={extension} module={type} extensionData={extensionData} />
                ) : (
                    <Renderer extension={extension} extensionData={extensionData} />
                )}
            </ExtensionProvider>
        </React.Fragment>
    ) : null;
};

interface WrapperProps {
    page: ForgePageName;
}

export const WithWrapper: React.FC<WrapperProps> = ({ page, children }) => {
    return page === FORGE_HELP_CENTER ? <StyledForgeContainer>{children}</StyledForgeContainer> : <>{children}</>;
};

export const ForgeRunnerModuleDumb: React.FC<ForgeRunnerProps & WithBlockedAppsBannerProps> = ({
    page,
    portalId,
    projectId,
    requestKey,
    requestTypeId,
    type,
    extensions,
    showBanner,
    resetBannerState,
}) => {
    const allowedExtensions: Extension[] = useShowBanner(extensions, page, true, showBanner, resetBannerState);

    return (
        <>
            {allowedExtensions.length > 0 && (
                <ExtensionPointProvider extensionType={type}>
                    {allowedExtensions.map(
                        (extension: Extension) =>
                            extension && (
                                <WithWrapper page={page} key={`${extension.id}-wrapper`}>
                                    <ForgeRunnerReactFragment
                                        key={extension.id}
                                        type={type}
                                        page={page}
                                        portalId={portalId}
                                        projectId={projectId}
                                        requestKey={requestKey}
                                        requestTypeId={requestTypeId}
                                        extension={extension}
                                    />
                                </WithWrapper>
                            )
                    )}
                </ExtensionPointProvider>
            )}
        </>
    );
};

export const ForgeRunnerModule = withExtensionList()(ForgeRunnerModuleDumb);

export default ForgeRunnerModule;
